const isBrowser = () => typeof document !== "undefined" && typeof window !== "undefined";

const serializeCookie = (
  key: string,
  value: string,
  options: { maxAgeSeconds?: number } = {}
) => {
  const segments = [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`];

  const maxAge = options.maxAgeSeconds ?? 60 * 60 * 24 * 365; // 1 year default for better persistence
  if (maxAge > 0) {
    segments.push(`Max-Age=${Math.floor(maxAge)}`);
  } else {
    segments.push("Max-Age=0");
  }

  segments.push("Path=/");
  segments.push("SameSite=Lax"); // Changed from Strict to Lax for better OAuth and refresh token compatibility

  if (isBrowser() && window.location.protocol === "https:") {
    segments.push("Secure");
  }

  return segments.join("; ");
};

const parseCookieString = (cookieString: string): Record<string, string> => {
  if (!cookieString) return {};

  return cookieString.split(/;\s*/).reduce<Record<string, string>>((acc, part) => {
    if (!part) return acc;
    const [rawKey, ...rawValue] = part.split("=");
    if (!rawKey) return acc;
    const key = decodeURIComponent(rawKey.trim());
    const value = rawValue.length > 0 ? rawValue.join("=") : "";
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {});
};

const sanitizeKey = (key: string): string => {
  if (!key.startsWith("sb-")) {
    return key;
  }

  return key.replace(/^sb-[a-zA-Z0-9_-]+-/, "__auth__");
};

const determineMaxAge = (rawValue: string): number | undefined => {
  try {
    if (!rawValue || rawValue === "undefined" || rawValue === "null") {
      return undefined;
    }

    // Attempt to parse. If it fails, it might be a simple string or corrupted.
    let parsed;
    try {
      parsed = JSON.parse(rawValue);
    } catch {
      // If parsing fails, it's not the JSON object we expect for a session.
      // It might be a legacy simple value or garbage.
      return undefined;
    }

    // Check if this is a session object with refresh token
    // Refresh tokens should be stored for longer duration
    if (parsed?.refresh_token) {
      // Store refresh token for 1 year to allow long-term sessions
      return 60 * 60 * 24 * 365;
    }

    // For access tokens, respect the expires_at time
    const expiresAt = parsed?.expires_at ?? parsed?.expiresAt;
    if (typeof expiresAt === "number") {
      const currentTime = Math.floor(Date.now() / 1000);
      const secondsRemaining = expiresAt - currentTime;

      // If token is expired or about to expire (< 60 seconds), 
      // still keep the cookie for a short time to allow Supabase to refresh it
      if (secondsRemaining < 60) {
        return 60 * 60; // Keep for 1 hour to allow refresh
      }

      return secondsRemaining;
    }
  } catch (_error) {
    // Ignore JSON parse issues – fall back to default (1 year)
  }
  return undefined;
};

class CookieStorage implements Storage {
  get length(): number {
    if (!isBrowser()) return 0;
    const cookies = parseCookieString(document.cookie);
    return Object.keys(cookies).length;
  }

  clear(): void {
    if (!isBrowser()) return;
    const cookies = parseCookieString(document.cookie);
    Object.keys(cookies).forEach((key) => {
      document.cookie = serializeCookie(key, "", { maxAgeSeconds: 0 });
    });
  }

  getItem(key: string): string | null {
    if (!isBrowser()) return null;
    const cookies = parseCookieString(document.cookie);
    const sanitizedKey = sanitizeKey(key);
    return Object.prototype.hasOwnProperty.call(cookies, sanitizedKey)
      ? cookies[sanitizedKey]
      : null;
  }

  key(index: number): string | null {
    if (!isBrowser()) return null;
    const keys = Object.keys(parseCookieString(document.cookie));
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    if (!isBrowser()) return;
    const sanitizedKey = sanitizeKey(key);
    document.cookie = serializeCookie(sanitizedKey, "", { maxAgeSeconds: 0 });
  }

  setItem(key: string, value: string): void {
    if (!isBrowser()) return;
    const sanitizedKey = sanitizeKey(key);
    const maxAgeSeconds = determineMaxAge(value);
    document.cookie = serializeCookie(sanitizedKey, value, { maxAgeSeconds });
  }
}

class InMemoryStorage implements Storage {
  private storage = new Map<string, string>();

  get length(): number {
    return this.storage.size;
  }

  clear(): void {
    this.storage.clear();
  }

  getItem(key: string): string | null {
    return this.storage.has(key) ? this.storage.get(key)! : null;
  }

  key(index: number): string | null {
    return Array.from(this.storage.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }
}

export const createAuthStorage = (): Storage => {
  if (!isBrowser()) {
    return new InMemoryStorage();
  }

  try {
    // Test cookie write permissions
    document.cookie = serializeCookie("__storage_test__", "1", { maxAgeSeconds: 1 });
    const isWritable = parseCookieString(document.cookie)["__storage_test__"] === "1";
    document.cookie = serializeCookie("__storage_test__", "", { maxAgeSeconds: 0 });

    if (isWritable) {
      return new CookieStorage();
    }
  } catch (_error) {
    // Fall through to in-memory storage
  }

  return new InMemoryStorage();
};
